import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClipboardList, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  create_user: 'Creazione Utente',
  approve_user: 'Approvazione Utente',
  update_user: 'Modifica Utente',
  delete_user: 'Eliminazione Utente',
  create_event: 'Creazione Evento',
  update_event: 'Modifica Evento',
  delete_event: 'Eliminazione Evento',
  approve_enrollment_free: 'Iscrizione Gratuita',
  request_payment: 'Richiesta Pagamento',
  reject_enrollment: 'Rifiuto Iscrizione',
};

const ACTION_COLORS = {
  create_user: 'bg-emerald-100 text-emerald-800',
  approve_user: 'bg-sky-100 text-sky-800',
  update_user: 'bg-amber-100 text-amber-800',
  delete_user: 'bg-red-100 text-red-800',
  create_event: 'bg-emerald-100 text-emerald-800',
  update_event: 'bg-amber-100 text-amber-800',
  delete_event: 'bg-red-100 text-red-800',
  approve_enrollment_free: 'bg-sky-100 text-sky-800',
  request_payment: 'bg-violet-100 text-violet-800',
  reject_enrollment: 'bg-red-100 text-red-800',
};

const ENTITY_LABELS = {
  user: 'Utente',
  event: 'Evento',
  enrollment: 'Iscrizione',
};

const PAGE_SIZE = 20;

export const AdminAuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [loading, setLoading] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterEntity !== 'all') params.append('entity_type', filterEntity);
      const { data } = await axios.get(`${API}/admin/audit-log?${params}`);
      setEntries(data.entries);
      setTotal(data.total);
    } catch {
      toast.error('Errore nel caricamento audit log');
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterEntity]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card data-testid="audit-log-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardList className="w-5 h-5 text-slate-600" />
            Audit Log Amministratore
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterEntity} onValueChange={(v) => { setFilterEntity(v); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="audit-entity-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="user">Utente</SelectItem>
                <SelectItem value="event">Evento</SelectItem>
                <SelectItem value="enrollment">Iscrizione</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(0); }}>
              <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="audit-action-filter">
                <SelectValue placeholder="Azione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchLog} disabled={loading} data-testid="audit-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && !loading && (
          <p className="text-center text-gray-500 py-8" data-testid="audit-empty">Nessuna voce nel registro audit</p>
        )}
        <div className="space-y-2">
          {entries.map((entry) => (
            <div key={entry.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded-lg border" data-testid={`audit-entry-${entry.id}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Badge className={`text-[10px] shrink-0 ${ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                  {ACTION_LABELS[entry.action] || entry.action}
                </Badge>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {ENTITY_LABELS[entry.entity_type] || entry.entity_type}
                </Badge>
                <span className="text-sm text-gray-900 truncate font-medium">
                  {entry.entity_name || '-'}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
                <span>{entry.admin_name}</span>
                <span>{formatDate(entry.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-3 border-t">
            <span className="text-xs text-gray-500">{total} voci totali</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAuditLog;
